import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Building2, Users, FolderKanban, Briefcase, Power, PowerOff, Crown, Clock, History, MoreVertical } from 'lucide-react'
import { toast } from 'sonner'
import { tenantsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Textarea } from '@/components/ui/textarea'
import type { TenantWithStats, CreateTenantRequest, UpdateTenantRequest, SubscriptionTier, UpdateTierRequest, ReactivateTenantRequest, SubscriptionHistory } from '@/types'

// Helper function to calculate days remaining in trial
function getTrialDaysRemaining(trialEndDate?: string): number {
  if (!trialEndDate) return 0
  const endDate = new Date(trialEndDate)
  const now = new Date()
  const diffTime = endDate.getTime() - now.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return Math.max(0, diffDays)
}

export default function AgenciesPage() {
  const queryClient = useQueryClient()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingTenant, setEditingTenant] = useState<TenantWithStats | null>(null)
  const [tierDialogTenant, setTierDialogTenant] = useState<TenantWithStats | null>(null)
  const [reactivateDialogTenant, setReactivateDialogTenant] = useState<TenantWithStats | null>(null)
  const [historyDialogTenant, setHistoryDialogTenant] = useState<TenantWithStats | null>(null)
  const [formData, setFormData] = useState({ tenant_name: '', admin_email: '', trial_days: '' })
  const [tierFormData, setTierFormData] = useState<{ subscription_tier: SubscriptionTier; change_reason: string; trial_days: string }>({
    subscription_tier: 'FREE_TRIAL',
    change_reason: '',
    trial_days: '',
  })
  const [reactivateFormData, setReactivateFormData] = useState<{ upgrade_to_premium: boolean; trial_days: string; reason: string }>({
    upgrade_to_premium: false,
    trial_days: '',
    reason: '',
  })

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['admin-tenants'],
    queryFn: () => tenantsApi.getAll(),
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateTenantRequest) => tenantsApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
      const adminUser = response?.admin_user
      if (adminUser) {
        toast.success(
          `Agency created successfully! Admin user created with login ID: ${adminUser.user_login_id}`,
          { duration: 6000 }
        )
      } else {
        toast.success('Agency created successfully')
      }
      setIsCreateDialogOpen(false)
      setFormData({ tenant_name: '', admin_email: '', trial_days: '' })
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to create agency')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTenantRequest }) => tenantsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
      toast.success('Agency updated successfully')
      setEditingTenant(null)
      setFormData({ tenant_name: '', admin_email: '', trial_days: '' })
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update agency')
    },
  })

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      tenantsApi.update(id, { is_active }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
      toast.success(`Agency ${variables.is_active ? 'activated' : 'deactivated'} successfully`)
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update agency status')
    },
  })

  const updateTierMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTierRequest }) => tenantsApi.updateTier(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
      toast.success('Subscription tier updated successfully')
      setTierDialogTenant(null)
      setTierFormData({ subscription_tier: 'FREE_TRIAL', change_reason: '', trial_days: '' })
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update subscription tier')
    },
  })

  const reactivateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReactivateTenantRequest }) => tenantsApi.reactivate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
      toast.success('Agency reactivated successfully')
      setReactivateDialogTenant(null)
      setReactivateFormData({ upgrade_to_premium: false, trial_days: '', reason: '' })
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to reactivate agency')
    },
  })

  // Query for subscription history
  const { data: subscriptionHistory = [] } = useQuery({
    queryKey: ['subscription-history', historyDialogTenant?.tenant_id],
    queryFn: () => historyDialogTenant ? tenantsApi.getSubscriptionHistory(historyDialogTenant.tenant_id) : Promise.resolve([]),
    enabled: !!historyDialogTenant,
  })

  const handleCreate = () => {
    if (!formData.tenant_name.trim()) {
      toast.error('Agency name is required')
      return
    }
    if (!formData.admin_email.trim()) {
      toast.error('Admin email is required')
      return
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.admin_email.trim())) {
      toast.error('Please enter a valid email address')
      return
    }
    const trialDays = formData.trial_days.trim() ? parseInt(formData.trial_days.trim()) : undefined
    if (trialDays !== undefined && (isNaN(trialDays) || trialDays <= 0)) {
      toast.error('Trial days must be a positive number')
      return
    }
    createMutation.mutate({
      tenant_name: formData.tenant_name.trim(),
      admin_email: formData.admin_email.trim(),
      trial_days: trialDays,
    })
  }

  const handleUpdate = () => {
    if (!editingTenant || !formData.tenant_name.trim()) {
      toast.error('Agency name is required')
      return
    }
    updateMutation.mutate({
      id: editingTenant.tenant_id,
      data: { tenant_name: formData.tenant_name.trim() },
    })
  }

  const openEditDialog = (tenant: TenantWithStats) => {
    setEditingTenant(tenant)
    setFormData({ tenant_name: tenant.tenant_name, admin_email: '', trial_days: '' })
  }

  const openTierDialog = (tenant: TenantWithStats) => {
    setTierDialogTenant(tenant)
    setTierFormData({
      subscription_tier: tenant.subscription_tier || 'FREE_TRIAL',
      change_reason: '',
      trial_days: '',
    })
  }

  const openReactivateDialog = (tenant: TenantWithStats) => {
    setReactivateDialogTenant(tenant)
    setReactivateFormData({
      upgrade_to_premium: false,
      trial_days: '',
      reason: '',
    })
  }

  const handleUpdateTier = () => {
    if (!tierDialogTenant) return
    const trialDays = tierFormData.trial_days.trim() ? parseInt(tierFormData.trial_days.trim()) : undefined
    if (trialDays !== undefined && (isNaN(trialDays) || trialDays <= 0)) {
      toast.error('Trial days must be a positive number')
      return
    }
    updateTierMutation.mutate({
      id: tierDialogTenant.tenant_id,
      data: {
        subscription_tier: tierFormData.subscription_tier,
        change_reason: tierFormData.change_reason.trim() || undefined,
        trial_days: trialDays,
      },
    })
  }

  const handleReactivate = () => {
    if (!reactivateDialogTenant) return
    const trialDays = reactivateFormData.trial_days.trim() ? parseInt(reactivateFormData.trial_days.trim()) : undefined
    if (trialDays !== undefined && (isNaN(trialDays) || trialDays <= 0)) {
      toast.error('Trial days must be a positive number')
      return
    }
    reactivateMutation.mutate({
      id: reactivateDialogTenant.tenant_id,
      data: {
        upgrade_to_premium: reactivateFormData.upgrade_to_premium,
        trial_days: trialDays,
        reason: reactivateFormData.reason.trim() || undefined,
      },
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Agencies</h1>
          <p className="text-muted-foreground">
            Manage all agencies/tenants in the system (Super Admin only)
          </p>
        </div>

        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Agency
        </Button>
      </div>

      <AgenciesTable
        tenants={tenants}
        isLoading={isLoading}
        onEdit={openEditDialog}
        onToggleStatus={(tenant) =>
          toggleStatusMutation.mutate({ id: tenant.tenant_id, is_active: !tenant.is_active })
        }
        isToggling={toggleStatusMutation.isPending}
        onChangeTier={openTierDialog}
        onReactivate={openReactivateDialog}
        onViewHistory={setHistoryDialogTenant}
      />

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Agency</DialogTitle>
            <DialogDescription>
              Add a new agency/tenant to the system. A default administrator user will be created with the provided email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tenant_name">Agency Name</Label>
              <Input
                id="tenant_name"
                value={formData.tenant_name}
                onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
                placeholder="Enter agency name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin_email">Admin Email</Label>
              <Input
                id="admin_email"
                type="email"
                value={formData.admin_email}
                onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                placeholder="Enter admin email address"
              />
              <p className="text-sm text-muted-foreground">
                A welcome email will be sent to this address with instructions to set up the password.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="trial_days">Trial Period (Days)</Label>
              <Input
                id="trial_days"
                type="number"
                min="1"
                value={formData.trial_days}
                onChange={(e) => setFormData({ ...formData, trial_days: e.target.value })}
                placeholder="14 (default)"
              />
              <p className="text-sm text-muted-foreground">
                Leave empty to use default 14-day trial period.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Agency'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingTenant} onOpenChange={(open) => !open && setEditingTenant(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Agency</DialogTitle>
            <DialogDescription>
              Update the agency name.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit_tenant_name">Agency Name</Label>
              <Input
                id="edit_tenant_name"
                value={formData.tenant_name}
                onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
                placeholder="Enter agency name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTenant(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tier Management Dialog */}
      <Dialog open={!!tierDialogTenant} onOpenChange={(open) => !open && setTierDialogTenant(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Subscription Tier</DialogTitle>
            <DialogDescription>
              Update the subscription tier for {tierDialogTenant?.tenant_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="subscription_tier">Subscription Tier</Label>
              <Select
                value={tierFormData.subscription_tier}
                onValueChange={(value: SubscriptionTier) =>
                  setTierFormData({ ...tierFormData, subscription_tier: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FREE_TRIAL">Free Trial</SelectItem>
                  <SelectItem value="PREMIUM">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {tierFormData.subscription_tier === 'FREE_TRIAL' && (
              <div className="space-y-2">
                <Label htmlFor="tier_trial_days">Trial Period (Days)</Label>
                <Input
                  id="tier_trial_days"
                  type="number"
                  min="1"
                  value={tierFormData.trial_days}
                  onChange={(e) => setTierFormData({ ...tierFormData, trial_days: e.target.value })}
                  placeholder="14 (default)"
                />
                <p className="text-sm text-muted-foreground">
                  Leave empty to use default 14-day trial period.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="change_reason">Reason for Change (Optional)</Label>
              <Textarea
                id="change_reason"
                value={tierFormData.change_reason}
                onChange={(e) => setTierFormData({ ...tierFormData, change_reason: e.target.value })}
                placeholder="Enter reason for tier change"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTierDialogTenant(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateTier} disabled={updateTierMutation.isPending}>
              {updateTierMutation.isPending ? 'Updating...' : 'Update Tier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivation Dialog */}
      <Dialog open={!!reactivateDialogTenant} onOpenChange={(open) => !open && setReactivateDialogTenant(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reactivate Agency</DialogTitle>
            <DialogDescription>
              Reactivate {reactivateDialogTenant?.tenant_name} with a new subscription
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reactivation Mode</Label>
              <Select
                value={reactivateFormData.upgrade_to_premium ? 'premium' : 'trial'}
                onValueChange={(value) =>
                  setReactivateFormData({ ...reactivateFormData, upgrade_to_premium: value === 'premium' })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>Reactivate with Free Trial</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="premium">
                    <div className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-amber-500" />
                      <span>Upgrade to Premium</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!reactivateFormData.upgrade_to_premium && (
              <div className="space-y-2">
                <Label htmlFor="reactivate_trial_days">Trial Period (Days)</Label>
                <Input
                  id="reactivate_trial_days"
                  type="number"
                  min="1"
                  value={reactivateFormData.trial_days}
                  onChange={(e) => setReactivateFormData({ ...reactivateFormData, trial_days: e.target.value })}
                  placeholder="14 (default)"
                />
                <p className="text-sm text-muted-foreground">
                  Leave empty to use default 14-day trial period.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="reactivate_reason">Reason for Reactivation (Optional)</Label>
              <Textarea
                id="reactivate_reason"
                value={reactivateFormData.reason}
                onChange={(e) => setReactivateFormData({ ...reactivateFormData, reason: e.target.value })}
                placeholder="Enter reason for reactivation"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReactivateDialogTenant(null)}>
              Cancel
            </Button>
            <Button onClick={handleReactivate} disabled={reactivateMutation.isPending}>
              {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate Agency'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription History Dialog */}
      <Dialog open={!!historyDialogTenant} onOpenChange={(open) => !open && setHistoryDialogTenant(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Subscription History</DialogTitle>
            <DialogDescription>
              Subscription tier history for {historyDialogTenant?.tenant_name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {subscriptionHistory.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No subscription history found.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tier</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptionHistory.map((history: SubscriptionHistory) => (
                    <TableRow key={history.history_id}>
                      <TableCell>
                        <Badge variant={history.tier === 'PREMIUM' ? 'default' : 'secondary'}>
                          {history.tier === 'PREMIUM' ? (
                            <><Crown className="h-3 w-3 mr-1" /> Premium</>
                          ) : (
                            <><Clock className="h-3 w-3 mr-1" /> Free Trial</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(history.start_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {history.end_date ? new Date(history.end_date).toLocaleDateString() : 'Current'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {history.change_reason || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialogTenant(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  )
}

function AgenciesTable({
  tenants,
  isLoading,
  onEdit,
  onToggleStatus,
  isToggling,
  onChangeTier,
  onReactivate,
  onViewHistory,
}: {
  tenants: TenantWithStats[]
  isLoading: boolean
  onEdit: (tenant: TenantWithStats) => void
  onToggleStatus: (tenant: TenantWithStats) => void
  isToggling: boolean
  onChangeTier: (tenant: TenantWithStats) => void
  onReactivate: (tenant: TenantWithStats) => void
  onViewHistory: (tenant: TenantWithStats) => void
}) {
  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>
  }

  if (tenants.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <Building2 className="mx-auto h-12 w-12 mb-4 opacity-50" />
          No agencies found. Click "Add Agency" to create one.
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Agency Name</TableHead>
            <TableHead className="text-center">Users</TableHead>
            <TableHead className="text-center">Projects</TableHead>
            <TableHead className="text-center">Clients</TableHead>
            <TableHead>Subscription</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="w-[180px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tenants.map((tenant) => {
            const daysRemaining = getTrialDaysRemaining(tenant.trial_end_date)
            const isTrialExpired = tenant.subscription_tier === 'FREE_TRIAL' && daysRemaining === 0

            return (
              <TableRow key={tenant.tenant_id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    {tenant.tenant_name}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    {tenant.user_count}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <FolderKanban className="h-4 w-4 text-muted-foreground" />
                    {tenant.project_count}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    {tenant.client_count}
                  </div>
                </TableCell>
                <TableCell>
                  {tenant.subscription_tier === 'PREMIUM' ? (
                    <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white">
                      <Crown className="h-3 w-3 mr-1" />
                      Premium
                    </Badge>
                  ) : (
                    <Badge variant={isTrialExpired ? 'destructive' : 'outline'}>
                      <Clock className="h-3 w-3 mr-1" />
                      {isTrialExpired ? 'Trial Expired' : `Trial (${daysRemaining}d left)`}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={tenant.is_active ? 'default' : 'secondary'}>
                    {tenant.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {new Date(tenant.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(tenant)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onChangeTier(tenant)}>
                        <Crown className="mr-2 h-4 w-4 text-amber-500" />
                        Change Tier
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onViewHistory(tenant)}>
                        <History className="mr-2 h-4 w-4 text-blue-500" />
                        View History
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {!tenant.is_active && (
                        <DropdownMenuItem onClick={() => onReactivate(tenant)}>
                          <Power className="mr-2 h-4 w-4 text-green-500" />
                          Reactivate
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => onToggleStatus(tenant)}
                        disabled={isToggling}
                      >
                        {tenant.is_active ? (
                          <>
                            <PowerOff className="mr-2 h-4 w-4 text-orange-500" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <Power className="mr-2 h-4 w-4 text-green-500" />
                            Activate
                          </>
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Card>
  )
}
